import { ModalityType } from '../generated-interfaces/graphql';
import { ParsedModifierGroup } from '../types/menu';
import { isSuppressedUpsell } from './groupedUpsellDeclined';
import logger from './logger';
import { CartItem, selectModifierInCartItem } from './cart';
import {
  logWithheldPromptSkipped,
  toConstraintKey,
} from '../redux/features/experiments/experiments.utils';
import {
  ALWAYS_OFFER,
  DO_NOT_UPSELL_IN_EXPRESS_MODE,
  SPEAK_TOTAL_IN_UPSELL,
} from './menuUnification.constants';

export interface UpsellConditionContext {
  cartItem: CartItem;
  ancestorNames: string[];
}

export interface UpsellConditionHandler {
  name: string;
  shouldAutoDecline(
    mg: ParsedModifierGroup,
    ctx: UpsellConditionContext
  ): boolean;
  // Also declines a required group the menu does not model as an upsell prompt.
  appliesToRequiredGroups?: boolean;
}

export const suppressUpsellHandler: UpsellConditionHandler = {
  name: 'SUPPRESS_UPSELL',
  shouldAutoDecline: (mg) => isSuppressedUpsell(mg.suppress_upsell_conditions),
};

// Express mode declines only the constraints the menu explicitly tags with
// `do_not_upsell_in_express_mode` — never untagged upsell prompts (HITL-1509).
// This is the single handler for both ways of entering express mode: the
// express meal period and the elapsed hitl_express_timer.
// `always_offer` overrides the tag: such a constraint must always be offered to
// the agent, so express mode never auto-declines it (HITL-1512). The exemption
// is scoped to express — SUPPRESS_UPSELL (the customer declined that family) and
// UPSELL_EXCLUDED_ITEM (menu-level incompatibility) still apply.
export const expressModeHandler: UpsellConditionHandler = {
  name: 'EXPRESS_MODE',
  shouldAutoDecline: (mg) =>
    !!mg.do_not_upsell_in_express_mode && !mg.always_offer,
};

// Is this a prompt express mode must never ask? Both fields are required: the
// tag alone is inert on a group the menu does not model as an upsell prompt,
// and every express path (the auto-decline above, the omnibar auto-prompt
// filter, the mod group heading click) gates on the same pair (HITL-1529).
export const isExpressSuppressedUpsellPrompt = (
  modGroup: Pick<
    ParsedModifierGroup,
    'is_upsell_prompt' | 'do_not_upsell_in_express_mode'
  >,
  isExpressMode: boolean
): boolean =>
  isExpressMode &&
  !!modGroup.is_upsell_prompt &&
  !!modGroup.do_not_upsell_in_express_mode;

export const excludedItemHandler: UpsellConditionHandler = {
  name: 'UPSELL_EXCLUDED_ITEM',
  shouldAutoDecline: (mg, ctx) => {
    if (!mg.upsell_excluded_items?.length) return false;
    const excluded = new Set(mg.upsell_excluded_items);
    return ctx.ancestorNames.some((a) => excluded.has(a));
  },
};

// A constraint withheld for this session's experiment group is declined, never asked.
const EXPERIMENT_WITHHELD = 'EXPERIMENT_WITHHELD';

export const experimentWithheldHandler = (
  withheldConstraints: string[]
): UpsellConditionHandler => {
  const withheld = new Set(withheldConstraints.map(toConstraintKey));
  return {
    name: EXPERIMENT_WITHHELD,
    shouldAutoDecline: (mg) => withheld.has(toConstraintKey(mg.name)),
    appliesToRequiredGroups: true,
  };
};

export const applyUpsellConditionsToCartItem = (
  cartItem: CartItem,
  modality: ModalityType,
  handlers: UpsellConditionHandler[],
  ancestorNames: string[] = []
): void => {
  if (handlers.length === 0) return;

  const currentAncestors = [...ancestorNames, cartItem.name?.toLowerCase()];
  const ctx: UpsellConditionContext = {
    cartItem,
    ancestorNames: currentAncestors,
  };

  Object.values(cartItem.modifierGroups ?? {}).forEach((mg) => {
    const isRequired = (mg.minimumSelections ?? 0) > 0;
    const eligibleHandlers = mg.is_upsell_prompt
      ? handlers
      : handlers.filter((h) => h.appliesToRequiredGroups && isRequired);

    const matchedHandlers = eligibleHandlers.filter((h) =>
      h.shouldAutoDecline(mg, ctx)
    );
    if (matchedHandlers.length === 0) return;

    const hasSelection =
      Object.keys(cartItem.childModifierGroups?.[mg.id]?.selectedItems ?? {})
        .length > 0;
    if (hasSelection) return;

    const declineItem = mg.upsell_declined_item
      ? Object.values(mg.menuItems ?? {}).find(
          (item) => item.name === mg.upsell_declined_item
        )
      : undefined;
    const handlerNames = matchedHandlers.map((h) => h.name).join(', ');
    if (!declineItem) {
      return;
    }

    selectModifierInCartItem(
      cartItem,
      declineItem.itemId,
      mg.id,
      true,
      '',
      {},
      {},
      modality,
      undefined,
      undefined,
      false
    );
    if (matchedHandlers.some((h) => h.name === EXPERIMENT_WITHHELD)) {
      logWithheldPromptSkipped(mg.name, 'auto-decline');
    }
    logger.debug(
      `[${handlerNames}] auto-declined "${declineItem.name}" in modGroup "${mg.name}" of cart item "${cartItem.name}" (cartItemId=${cartItem.cartItemId})`
    );
  });

  Object.values(cartItem.childModifierGroups ?? {}).forEach((childGroup) => {
    Object.values(childGroup.selectedItems ?? {}).forEach((nestedItem) => {
      applyUpsellConditionsToCartItem(
        nestedItem,
        modality,
        handlers,
        currentAncestors
      );
    });
  });
};

export interface ParsedUpsellConditions {
  suppress_upsell_conditions: string[];
  do_not_upsell_in_express_mode: boolean;
  always_offer: boolean;
  upsell_excluded_items: string[];
}

export const parseUpsellConditions = (
  flattenedConditions: string[]
): ParsedUpsellConditions => ({
  suppress_upsell_conditions: flattenedConditions.filter((c) =>
    /^suppress_upsell\.\w+$/.test(c)
  ),
  do_not_upsell_in_express_mode: flattenedConditions.includes(
    DO_NOT_UPSELL_IN_EXPRESS_MODE
  ),
  // Matched case-insensitively (like `upsell_excluded_item` and
  // CONSTRAINT_ITEM_NOT_ON_CHECK): this tag is hand-authored per constraint, and
  // a casing typo would fail closed — the upsell would be suppressed again with
  // no signal anywhere.
  always_offer: flattenedConditions.some(
    (c) => c.toLowerCase() === ALWAYS_OFFER
  ),
  upsell_excluded_items: flattenedConditions
    .filter((c) => /^upsell_excluded_item:/i.test(c))
    .flatMap((c) =>
      c
        .replace(/^upsell_excluded_item:/i, '')
        .split(',')
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean)
    ),
});

// HITL-1570: does the constraint carry the `speak_total_in_upsell` condition?
// Matched case-insensitively like the other hand-authored tags, and by exact
// name — a legacy `speak_total_in_upsell_fallback:` entry must not count.
export const hasSpeakTotalInUpsellCondition = (
  conditions?: string[]
): boolean =>
  (conditions ?? [])
    .flatMap((c) => c.split('\n'))
    .some((c) => c.trim().toLowerCase() === SPEAK_TOTAL_IN_UPSELL);
